<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import VueHcaptcha from '@hcaptcha/vue3-hcaptcha';
import AuthLayout from '../../../components/AuthLayout.vue';
import AuthHeading from '../../../components/AuthHeading.vue';
import AuthButton from '../../../components/AuthButton.vue';
import AuthShowcase from '../../../components/AuthShowcase.vue';
import { resendConfirmation } from '../../../api/auth';

const props = defineProps({
  email: {
    type: String,
    default: '',
  },
});

const { t } = useI18n();
const router = useRouter();
const store = useStore();

if (!props.email) {
  router.push({ name: 'login' });
}

const globalConfig = computed(() => store.getters['globalConfig/get']);
const isResendingEmail = ref(false);
const hCaptcha = ref(null);
let captchaToken = '';

const performResend = async () => {
  isResendingEmail.value = true;
  try {
    await resendConfirmation({
      email: props.email,
      hCaptchaClientResponse: captchaToken,
    });
    useAlert(t('REGISTER.VERIFY_EMAIL.RESEND_SUCCESS'));
  } catch {
    useAlert(t('REGISTER.VERIFY_EMAIL.RESEND_ERROR'));
  } finally {
    isResendingEmail.value = false;
    captchaToken = '';
    if (globalConfig.value.hCaptchaSiteKey) {
      hCaptcha.value.reset();
    }
  }
};

const handleResendEmail = () => {
  if (isResendingEmail.value) return;
  if (globalConfig.value.hCaptchaSiteKey) {
    hCaptcha.value.execute();
  } else {
    performResend();
  }
};

const onCaptchaVerified = token => {
  captchaToken = token;
  performResend();
};

const onCaptchaError = () => {
  isResendingEmail.value = false;
  captchaToken = '';
  hCaptcha.value.reset();
};
</script>

<template>
  <AuthLayout>
    <AuthHeading
      :kicker="$t('TALKROBIN_AUTH.VERIFY.KICKER')"
      :title="$t('TALKROBIN_AUTH.VERIFY.TITLE')"
      :highlight="$t('TALKROBIN_AUTH.VERIFY.HIGHLIGHT')"
      :description="$t('REGISTER.VERIFY_EMAIL.DESCRIPTION', { email })"
    />
    <div class="space-y-4">
      <VueHcaptcha
        v-if="globalConfig.hCaptchaSiteKey"
        ref="hCaptcha"
        size="invisible"
        :sitekey="globalConfig.hCaptchaSiteKey"
        @verify="onCaptchaVerified"
        @error="onCaptchaError"
        @expired="onCaptchaError"
        @challenge-expired="onCaptchaError"
        @closed="onCaptchaError"
      />
      <AuthButton
        type="button"
        data-testid="resend_email_button"
        :show-arrow="false"
        :label="$t('REGISTER.VERIFY_EMAIL.RESEND')"
        :is-loading="isResendingEmail"
        @click="handleResendEmail"
      />
    </div>
    <template #aside>
      <AuthShowcase
        :title="$t('TALKROBIN_AUTH.SIGNUP.SHOWCASE_TITLE')"
        :highlight="$t('TALKROBIN_AUTH.SIGNUP.SHOWCASE_HIGHLIGHT')"
      />
    </template>
  </AuthLayout>
</template>
